import { zodResolver } from '@hookform/resolvers/zod';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import {
  ApiError,
  type VonqContractChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { useAddContractContext } from '@/contexts/add-contract-context';
import { queryKeys } from '@/query-keys';

import { buildOptionalRequestFields } from './build-optional-request-fields';
import type { ContractFormFields, ContractSubmitSuccess } from './types';
import { useValidationSchema } from './use-validation-schema';

type CreateVonqContractPayload = Parameters<
  typeof SourcingService.postInternalApiSourcingVonqContractsNew
>[0];

interface ContractSubmitPayload {
  requestBody: CreateVonqContractPayload;
  logoSquareUrl: string | null;
  logoRectangleUrl: string | null;
}

// Hire's reason can run to two sentences that tell the recruiter not to retry.
const REASON_TOAST_DURATION = 10_000;

// Hire names why the contract failed, VONQ's own refusal included, so its
// message outranks ours.
function contractErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body: unknown = error.body;
  if (typeof body !== 'object' || body == null || !('error_message' in body))
    return null;
  const message = body.error_message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

interface UseContractFormProps {
  channelId: number;
  onSubmitSuccess: (result: ContractSubmitSuccess) => void;
}

function buildContractSubmitPayload({
  channelId,
  channelName,
  channelDetails,
  formValues,
  contractCredentials,
}: {
  channelId: number;
  channelName: string | undefined;
  channelDetails: VonqContractChannelDetails;
  formValues: ContractFormFields;
  contractCredentials: VonqContractChannelDetails['contract_credentials'];
}): ContractSubmitPayload | null {
  const resolvedChannelName = channelName ?? channelDetails.name;
  if (!resolvedChannelName) return null;

  const logoSquare = channelDetails.logo_square_url ?? '';
  const logoRectangle = channelDetails.logo_rectangle_url ?? '';

  return {
    requestBody: {
      channel_id: String(channelId),
      channel_name: resolvedChannelName,
      logo_rectangle: logoRectangle,
      logo_square: logoSquare,
      ...(contractCredentials.length > 0 && {
        credentials: formValues.credentials,
      }),
      ...buildOptionalRequestFields(formValues),
      followed_instructions: formValues.followedInstructions ?? false,
    },
    logoSquareUrl: logoSquare,
    logoRectangleUrl: logoRectangle,
  };
}

/**
 * Hook for the add-contract form. Call inside a Suspense boundary once
 * `channelId` is set.
 */
export function useContractForm({
  channelId,
  onSubmitSuccess,
}: UseContractFormProps) {
  const intl = useIntl();
  const { notifyError } = useToaster();
  const queryClient = useQueryClient();
  const { channelName, isSheetOpen } = useAddContractContext();
  const openRef = useRef(isSheetOpen);

  const { data: channelDetails } = useSuspenseQuery({
    queryKey: [...queryKeys.settings.contracts.vonqChannelDetails, channelId],
    queryFn: () =>
      SourcingService.getInternalApiSourcingVonqContractsChannelDetails(
        channelId,
      ),
  });
  const contractCredentials = channelDetails.contract_credentials;

  const validationSchema = useValidationSchema({
    requiredCredentialNames: contractCredentials.map(
      credential => credential.name,
    ),
    manualSetupRequired: channelDetails.manual_setup_required,
  });

  const initialFormValues = useMemo<ContractFormFields>(
    () => ({
      alias: '',
      amount: '',
      currency: '',
      credits: '',
      postingDurationDays: '7',
      credentials: {},
      followedInstructions: false,
    }),
    [],
  );

  const hookForm = useForm<ContractFormFields>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialFormValues,
    mode: 'onChange',
  });

  const { handleSubmit } = hookForm;

  useEffect(() => {
    openRef.current = isSheetOpen;
  }, [isSheetOpen]);

  const { mutate: createContract, isPending: isSubmitting } = useMutation({
    mutationFn: ({ requestBody }: ContractSubmitPayload) =>
      SourcingService.postInternalApiSourcingVonqContractsNew(requestBody),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.settings.contracts.vonqContracts],
      });
      if (!openRef.current) return;
      onSubmitSuccess({
        name: variables.requestBody.channel_name,
        logoSquareUrl: variables.logoSquareUrl,
        logoRectangleUrl: variables.logoRectangleUrl,
      });
    },
    onError: error => {
      const reason = contractErrorMessage(error);
      if (reason != null) {
        notifyError({ message: reason, duration: REASON_TOAST_DURATION });
        return;
      }
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to add contract',
          description: 'Error toast when Vonq contract creation API fails',
        }),
      });
    },
  });

  const handleFormSubmit = handleSubmit(formValues => {
    const payload = buildContractSubmitPayload({
      channelId,
      channelName,
      channelDetails,
      formValues,
      contractCredentials,
    });

    if (payload == null) {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to add contract',
          description: 'Error toast when Vonq contract creation API fails',
        }),
      });
      return;
    }

    createContract(payload);
  });

  return {
    hookForm,
    channelDetails,
    handleFormSubmit,
    isSubmitting,
  };
}
